/**
 * Shared Parameter Store
 *
 * Manages the state of shared parameters loaded from file.
 * Provides parsed parameters for use in dropdowns throughout the app.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { getStorage } from '../lib/utils/safeStorage';
import type {
  SharedParameter,
  SharedParameterConfiguration,
  ParameterOption,
  GroupedParameterOptions,
} from '../types/sharedParameter.types';
import {
  parseSharedParameterFile,
  parametersToOptions,
  groupParameterOptions,
  validateSharedParameterFile,
} from '../services/sharedParameterParser';

// Default shared parameter file content (embedded for browser context)
// This is the parsed version of PipeEX_SharedParameters.txt
import { DEFAULT_SHARED_PARAMETERS } from '../constants/defaultSharedParameters';

// ============================================================================
// State Interface
// ============================================================================

interface SharedParameterState extends SharedParameterConfiguration {
  // Computed/derived values
  parameterOptions: ParameterOption[];
  groupedOptions: GroupedParameterOptions[];
}

// ============================================================================
// Actions Interface
// ============================================================================

interface SharedParameterActions {
  /** Set whether to use the default file */
  setUseDefaultFile: (useDefault: boolean) => void;

  /** Set the custom file path */
  setFilePath: (path: string) => void;

  /** Load and parse file content */
  loadFileContent: (content: string) => void;

  /** Load default parameters */
  loadDefaultParameters: () => void;

  /** Reset to initial state */
  reset: () => void;

  /** Get parameters filtered by group */
  getParametersByGroup: (groupName: string) => SharedParameter[];

  /** Get parameter options for a specific group */
  getOptionsByGroup: (groupName: string) => ParameterOption[];

  /** Search parameters by name */
  searchParameters: (query: string) => SharedParameter[];
}

// ============================================================================
// Initial State
// ============================================================================

const initialState: SharedParameterState = {
  filePath: 'PipeEX_SharedParameters.txt',
  useDefaultFile: true,
  fileContent: null,
  parsedParameters: [],
  parsedGroups: [],
  isLoading: false,
  error: null,
  isParsed: false,
  parameterOptions: [],
  groupedOptions: [],
};

const STORAGE_VERSION = 2;

// ============================================================================
// Store Creation
// ============================================================================

export const useSharedParameterStore = create<SharedParameterState & SharedParameterActions>()(
  persist(
    (set, get) => ({
      ...initialState,

      setUseDefaultFile: (useDefault) => {
        set({ useDefaultFile: useDefault });
        if (useDefault) {
          get().loadDefaultParameters();
        }
      },

      setFilePath: (path) => {
        set({ filePath: path });
      },

      loadFileContent: (content) => {
        set({ isLoading: true, error: null });

        try {
          // Validate first
          const validation = validateSharedParameterFile(content);
          if (!validation.isValid) {
            set({
              isLoading: false,
              error: validation.error || 'Invalid file format',
              isParsed: false,
            });
            return;
          }

          // Parse the file
          const parsed = parseSharedParameterFile(content);

          // Generate options
          const parameterOptions = parametersToOptions(parsed.parameters);
          const groupedOptions = groupParameterOptions(parsed.parameters);

          set({
            fileContent: content,
            parsedParameters: parsed.parameters,
            parsedGroups: parsed.groups,
            parameterOptions,
            groupedOptions,
            isLoading: false,
            error: null,
            isParsed: true,
          });
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to parse file',
            isParsed: false,
          });
        }
      },

      loadDefaultParameters: () => {
        set({ isLoading: true, error: null });

        try {
          // Parse the default parameters
          const parsed = parseSharedParameterFile(DEFAULT_SHARED_PARAMETERS);

          // Generate options
          const parameterOptions = parametersToOptions(parsed.parameters);
          const groupedOptions = groupParameterOptions(parsed.parameters);

          set({
            filePath: 'PipeEX_SharedParameters.txt',
            useDefaultFile: true,
            parsedParameters: parsed.parameters,
            parsedGroups: parsed.groups,
            parameterOptions,
            groupedOptions,
            isLoading: false,
            error: null,
            isParsed: true,
          });
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to load default parameters',
            isParsed: false,
          });
        }
      },

      reset: () => {
        set(initialState);
      },

      getParametersByGroup: (groupName) => {
        const { parsedParameters, parsedGroups } = get();
        const group = parsedGroups.find((g) => g.name === groupName);
        if (!group) return [];
        return parsedParameters.filter((p) => p.groupId === group.id);
      },

      getOptionsByGroup: (groupName) => {
        const { groupedOptions } = get();
        const group = groupedOptions.find((g) => g.groupName === groupName);
        return group?.options || [];
      },

      searchParameters: (query) => {
        const { parsedParameters } = get();
        const lowerQuery = query.toLowerCase();
        return parsedParameters.filter(
          (p) =>
            p.name.toLowerCase().includes(lowerQuery) ||
            p.description.toLowerCase().includes(lowerQuery)
        );
      },
    }),
    {
      name: 'pipeex-shared-parameters',
      version: STORAGE_VERSION,
      storage: createJSONStorage(getStorage),
      partialize: (state) => ({
        filePath: state.filePath,
        useDefaultFile: state.useDefaultFile,
        fileContent: state.fileContent,
      }),
      migrate: (persistedState: any, version) => {
        if (!persistedState || typeof persistedState !== 'object') {
          return {
            filePath: 'PipeEX_SharedParameters.txt',
            useDefaultFile: true,
            fileContent: null,
          };
        }

        return {
          filePath:
            typeof persistedState.filePath === 'string'
              ? persistedState.filePath
              : 'PipeEX_SharedParameters.txt',
          useDefaultFile: persistedState.useDefaultFile ?? true,
          fileContent:
            version >= 2 && typeof persistedState.fileContent === 'string'
              ? persistedState.fileContent
              : null,
        };
      },
      onRehydrateStorage: () => {
        return (state) => {
          // After rehydration, load the appropriate parameters
          if (state?.useDefaultFile) {
            state.loadDefaultParameters();
            return;
          }

          if (typeof state?.fileContent === 'string' && state.fileContent.trim() !== '') {
            state.loadFileContent(state.fileContent);
          }
        };
      },
    }
  )
);

// ============================================================================
// Selector Hooks
// ============================================================================

export const useSharedParameters = () =>
  useSharedParameterStore((state) => state.parsedParameters);

export const useSharedParameterGroups = () =>
  useSharedParameterStore((state) => state.parsedGroups);

export const useParameterOptions = () =>
  useSharedParameterStore((state) => state.parameterOptions);

export const useGroupedParameterOptions = () =>
  useSharedParameterStore((state) => state.groupedOptions);

export const useSharedParameterLoading = () =>
  useSharedParameterStore((state) => state.isLoading);

export const useSharedParameterError = () =>
  useSharedParameterStore((state) => state.error);

export const useSharedParameterIsParsed = () =>
  useSharedParameterStore((state) => state.isParsed);
